import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import {
    Button,
    Icon,
    Loader,
    Message,
    Pagination,
    Search,
    Segment,
    SegmentGroup,
    Table,
} from "semantic-ui-react";
import { downloadIcsAplicationsAsCsv, getIcsAplicationsAsync } from "../../api/certificates";
import { getICSCertificateStatus, IcsAplication } from "../../models/certificate";
import { idDataSetsFilterInit } from "../../models/crewMember";
import { formatDate } from "../../models/common";
import { useHistory, useLocation } from "react-router-dom";
import { IcsAplicationsAdvanceFilterForm } from "./IcsAplicationsAdvanceFilterForm";
import "./CertificateForm/AdvanceSearch.css";
import { useSelector } from "react-redux";
import { AppState } from "../../store/configureStore";
import { SpecificExportsModal } from "../SpecificExportsModal/SpecificExportsModal";
import { DebounceInput } from "react-debounce-input";

export const IcsAplicationView = () => {
    const intl = useIntl();
    const loc = useLocation();
    const [filters, setFilters] = useState(idDataSetsFilterInit);
    const [tableData, setTableData] = useState<IcsAplication[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(loc.state ? (loc.state as number) : 1);
    const [openModal, setOpenModal] = useState(false);
    const [exportsModalOpen, setExportsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const user = useSelector((state: AppState) => state.user);

    const getIcsAplications = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getIcsAplicationsAsync(filters);
            setTableData(res.result);
            setTotalRows(res.totalRows);
        } catch {
            setTableData([]);
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    const downloadCsv = async () => {
        await downloadIcsAplicationsAsCsv(filters);
    };

    useEffect(() => {
        getIcsAplications();
    }, [getIcsAplications]);

    return (
        <>
            <div className="container-for-buttons">
                <div className="left-button">
                    <Button
                        primary
                        onClick={() => setExportsModalOpen(true)}
                        content={intl.formatMessage({ id: "specificExports" })}
                    />
                    <Button
                        primary
                        onClick={downloadCsv}
                        content={intl.formatMessage({ id: "downloadCsv" })}
                    />
                </div>
                <DebounceInput
                    id="ics-searchbar"
                    minLength={1}
                    debounceTimeout = {500}
                    placeholder={intl.formatMessage({ id: "search" })}
                    value = {filters.quickSearch}
                    onChange={event => {
                        setFilters({
                            ...filters,
                            quickSearch: event.target.value,
                            currentLanguage: user.language,
                        });
                    }}
                />
                <Button
                    primary
                    onClick={() => setFilters(idDataSetsFilterInit)}
                    content={intl.formatMessage({ id: "clearFilter" })}
                />
                <Button
                    primary
                    onClick={() => setOpenModal(true)}
                    content={intl.formatMessage({ id: "advanceSearch" })}
                />
            </div>
            <SegmentGroup>
                {loading ? (
                    <Loader active inline="centered" size="huge" />
                ) : (
                    <>
                        {tableData.length === 0 ? (
                            <Message
                                icon="info circle"
                                header={intl.formatMessage({ id: "noResultsFound" })}
                            />
                        ) : (
                            <Segment>
                                <Table sortable basic="very" compact="very">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "qualificationID"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "qualificationID",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "qualificationID" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "icNumber"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "icNumber",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "icNumber" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "lastName"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "lastName",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "lastName" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "firstName"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "firstName",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "firstName" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "areaOfValidity"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "areaOfValidity",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "areaOfValidity" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "examinationOrganisation"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "examinationOrganisation",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({
                                                    id: "examinationOrganisation",
                                                })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "dateOfReceipt"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "dateOfReceipt",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "dateOfReceipt" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                width={1}
                                                sorted={
                                                    filters.column === "dateOfIssue"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "dateOfIssue",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "dateOfIssue" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                sorted={
                                                    filters.column === "comments"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "comments",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "comments" })}
                                            </Table.HeaderCell>
                                            <Table.HeaderCell
                                                width={1}
                                                sorted={
                                                    filters.column === "status"
                                                        ? filters.descDirection
                                                            ? "ascending"
                                                            : "descending"
                                                        : undefined
                                                }
                                                onClick={() =>
                                                    setFilters({
                                                        ...filters,
                                                        column: "status",
                                                        descDirection: !filters.descDirection,
                                                    })
                                                }
                                            >
                                                {intl.formatMessage({ id: "status" })}
                                            </Table.HeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {tableData.map(
                                            ({
                                                userGuid,
                                                qualificationID,
                                                administrativeNumber,
                                                lastName,
                                                firstName,
                                                areaOfValidity,
                                                examinationOrganization,
                                                dateOfReceipt,
                                                dateOfIssue,
                                                comments,
                                                applicationStatus,
                                                status,
                                                canceled
                                            }: IcsAplication) => (
                                                <Table.Row
                                                    key={administrativeNumber}
                                                    onClick={() =>
                                                        history.push(`/crew-members/${userGuid}`)
                                                    }
                                                    className="table-row"
                                                >
                                                    <Table.Cell>{administrativeNumber}</Table.Cell>
                                                    <Table.Cell>{qualificationID}</Table.Cell>
                                                    <Table.Cell>{lastName}</Table.Cell>
                                                    <Table.Cell>{firstName}</Table.Cell>
                                                    <Table.Cell>{areaOfValidity}</Table.Cell>
                                                    <Table.Cell>
                                                        {examinationOrganization}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        {!dateOfReceipt ||
                                                        dateOfReceipt === "0001-01-01T00:00:00"
                                                            ? ""
                                                            : formatDate(dateOfReceipt)}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        {!dateOfIssue ||
                                                        dateOfIssue === "0001-01-01T00:00:00"
                                                            ? ""
                                                            : formatDate(dateOfIssue)}
                                                    </Table.Cell>
                                                    <Table.Cell className="table-cell">
                                                        {comments}
                                                    </Table.Cell>
                                                    <Table.Cell>
                                                        {getICSCertificateStatus(
                                                            canceled,
                                                            applicationStatus!,
                                                            status!
                                                        )}
                                                    </Table.Cell>
                                                </Table.Row>
                                            )
                                        )}
                                    </Table.Body>
                                </Table>
                            </Segment>
                        )}
                    </>
                )}
                {totalRows > 50 && (
                    <Segment>
                        <Pagination
                            size="small"
                            activePage={page}
                            onPageChange={(e, data: any) => {
                                setPage(+data.activePage);
                                setFilters({
                                    ...filters,
                                    page: +data.activePage,
                                });
                            }}
                            ellipsisItem={{
                                content: <Icon name="ellipsis horizontal" />,
                            }}
                            firstItem={{ content: <Icon name="angle double left" /> }}
                            lastItem={{ content: <Icon name="angle double right" /> }}
                            prevItem={{ content: <Icon name="angle left" /> }}
                            nextItem={{ content: <Icon name="angle right" /> }}
                            totalPages={Math.ceil(totalRows / 50)}
                        />
                    </Segment>
                )}
            </SegmentGroup>

            <IcsAplicationsAdvanceFilterForm
                open={openModal}
                queryFilter={filters}
                setQueryFilter={setFilters}
                handleClose={() => setOpenModal(false)}
            />

            <SpecificExportsModal
                open={exportsModalOpen}
                close={() => setExportsModalOpen(false)}
            />
        </>
    );
};
